import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressLoyaltyEarningruleLoyaltyEarningRulesUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
    '*.pub.wix-code.com': [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/loyalty-earning-rules',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/loyalty-earning-rules',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_loyalty_earning-rules';

/**
 * Creates a non-automated earning rule.
 *
 * >**Note**: You can only create non-automated earning rules from a supported list.
 * For the supported list of services, see the Introduction.
 */
export function createLoyaltyEarningRule(
  payload: object
): RequestOptionsFactory<any> {
  function __createLoyaltyEarningRule({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'earningRule.createdDate' },
          { path: 'earningRule.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.loyalty_earning_rule',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.earningrule.LoyaltyEarningRules.CreateLoyaltyEarningRule',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyEarningruleLoyaltyEarningRulesUrl({
        protoPath: '/v1/earning-rules',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'earningRule.createdDate' },
              { path: 'earningRule.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createLoyaltyEarningRule;
}

/**
 * Creates multiple non-automated earning rules.
 *
 * >**Note**: You can only create non-automated earning rules from a supported list.
 * For the supported list of services, see the Introduction.
 */
export function bulkCreateLoyaltyEarningRules(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateLoyaltyEarningRules({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'earningRules.createdDate' },
          { path: 'earningRules.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.loyalty_earning_rule',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.earningrule.LoyaltyEarningRules.BulkCreateLoyaltyEarningRules',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyEarningruleLoyaltyEarningRulesUrl({
        protoPath: '/v1/bulk/earning-rules/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateLoyaltyEarningRules;
}

/**
 * Creates a custom automated earning rule.
 *
 * To learn more about the automated rules, see the Introduction.
 */
export function createCustomLoyaltyEarningRule(
  payload: object
): RequestOptionsFactory<any> {
  function __createCustomLoyaltyEarningRule({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.loyalty_earning_rule',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.earningrule.LoyaltyEarningRules.CreateCustomLoyaltyEarningRule',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyEarningruleLoyaltyEarningRulesUrl({
        protoPath: '/v1/earning-rules/custom',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'earningRule.createdDate' },
              { path: 'earningRule.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCustomLoyaltyEarningRule;
}

/**
 * Retrieves a specified non-automated earning rule.
 *
 * To retrieve both automated and non-automated earning rules, call List Earning Rules.
 */
export function getLoyaltyEarningRule(
  payload: object
): RequestOptionsFactory<any> {
  function __getLoyaltyEarningRule({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.loyalty_earning_rule',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.earningrule.LoyaltyEarningRules.GetLoyaltyEarningRule',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyEarningruleLoyaltyEarningRulesUrl({
        protoPath: '/v1/earning-rules/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'earningRule.createdDate' },
              { path: 'earningRule.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getLoyaltyEarningRule;
}

/**
 * Updates an earning rule.
 *
 * Supports partial updates.
 *
 * Revision number, which increments by 1 each time the earning rule is updated. To prevent conflicting changes,
 * the current `revision` must be passed when updating the earning rule.
 */
export function updateLoyaltyEarningRule(
  payload: object
): RequestOptionsFactory<any> {
  function __updateLoyaltyEarningRule({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'earningRule.createdDate' },
          { path: 'earningRule.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.loyalty_earning_rule',
      method: 'PUT' as any,
      methodFqn:
        'com.wixpress.loyalty.earningrule.LoyaltyEarningRules.UpdateLoyaltyEarningRule',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyEarningruleLoyaltyEarningRulesUrl({
        protoPath: '/v1/earning-rules/{earningRule.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'earningRule.createdDate' },
              { path: 'earningRule.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateLoyaltyEarningRule;
}

/**
 * Deletes a non-automated earning rule.
 *
 * To delete an automated earning rule, call
 * Delete Automation Earning Rule.
 *
 * To update an earning rule's status instead of deleting it, call
 * Update Loyalty Earning Rule.
 */
export function deleteLoyaltyEarningRule(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteLoyaltyEarningRule({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.loyalty_earning_rule',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.loyalty.earningrule.LoyaltyEarningRules.DeleteLoyaltyEarningRule',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyEarningruleLoyaltyEarningRulesUrl({
        protoPath: '/v1/earning-rules/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteLoyaltyEarningRule;
}

/**
 * Deletes a custom automated earning rule.
 * Pre-installed automated rules can only be paused, not deleted.
 *
 * To update an earning rule's status instead of deleting it, call
 * Update Loyalty Earning Rule.
 *
 * To delete a non-automated earning rule, call
 * Delete Loyalty Earning Rule.
 */
export function deleteAutomationEarningRule(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteAutomationEarningRule({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.loyalty_earning_rule',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.loyalty.earningrule.LoyaltyEarningRules.DeleteAutomationEarningRule',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyEarningruleLoyaltyEarningRulesUrl({
        protoPath: '/v1/automation-earning-rules/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteAutomationEarningRule;
}

/**
 * Retrieves a list of earning rules.
 *
 * Returns both automated and non-automated earning rules.
 *
 * You can filter the results by `triggerAppId` or `triggerActivityType`.
 */
export function listEarningRules(payload: object): RequestOptionsFactory<any> {
  function __listEarningRules({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.loyalty_earning_rule',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.earningrule.LoyaltyEarningRules.ListEarningRules',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyEarningruleLoyaltyEarningRulesUrl({
        protoPath: '/v1/earning-rules/rules',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'earningRules.createdDate' },
              { path: 'earningRules.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listEarningRules;
}
